'use client';

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User } from './postgres';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User | null>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function PostgresAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Vérifier si l'utilisateur est admin (level = 1)
  const isAdmin = user?.level === 1 || false;

  // Restaurer uniquement une session que le serveur reconnaît encore.
  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const loadUser = async () => {
      try {
        const response = await fetch('/api/auth/session', {
          cache: 'no-store',
          credentials: 'same-origin',
        });

        if (!response.ok) {
          window.localStorage.removeItem('admin_postgres_user');
          setUser(null);
          return;
        }

        const { user: sessionUser } = await response.json();
        window.localStorage.setItem('admin_postgres_user', JSON.stringify(sessionUser));
        setUser(sessionUser);
      } catch (error) {
        console.error('Erreur lors de la vérification de la session administrateur:', error);
        window.localStorage.removeItem('admin_postgres_user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Fonction de connexion
  const signIn = async (email: string, password: string): Promise<User | null> => {
    try {
      setLoading(true);
      
      // Appel à l'API de connexion
      const response = await fetch('/api/auth/signin', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Échec de la connexion');
      }

      const userData = await response.json();
      
      // Vérifier que l'utilisateur est admin (level = 1)
      if (userData.level !== 1) {
        throw new Error('Accès refusé. Seuls les administrateurs peuvent accéder à cette interface.');
      }
      
      // Stocker l'utilisateur
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('admin_postgres_user', JSON.stringify(userData));
      }
      setUser(userData);
      
      return userData;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const signOut = async (): Promise<void> => {
    try {
      // Appel à l'API de déconnexion avec les informations utilisateur pour le logging
      await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.id || null,
          email: user?.email || null,
        }),
      });
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    } finally {
      // Nettoyer le stockage local
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('admin_postgres_user');
      }
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function usePostgresAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('usePostgresAuth must be used within a PostgresAuthProvider');
  }
  return context;
}
